// EdgeLexPluginRegistry.ts - TypeScript Plugin Contract Validator for EdgeLex
// Ensures hydration-safe, order-preserving, and strictly typed plugin initialization

// Import all plugin contracts
import { CaptionNodeContract } from './contracts/PluginContract_CaptionNode';
import { ClauseNodeContract } from './contracts/PluginContract_ClauseNode';
import { TableNodeContract } from './contracts/PluginContract_TableNode';
import { LineNumberedParagraphContract } from './contracts/PluginContract_LineNumberedParagraphNode';
import { LegalSectionContract } from './contracts/PluginContract_LegalSectionNode';
import { LDSPCompletionContract } from './contracts/PluginContract_LDSPCompletion';
import { ParagraphIndentContract } from './contracts/PluginContract_ParagraphIndentPlugin';
import { LinkNodeContract } from './contracts/PluginContract_LinkNode';
import { QuoteNodeContract } from './contracts/PluginContract_QuoteNode';
import { HeadingNodeContract } from './contracts/PluginContract_HeadingNode';
import { CodeNodeContract } from './contracts/PluginContract_CodeNode';

// 🔧 TYPE DEFINITIONS
export interface PluginContract {
  pluginName: string;
  nodeTypes: string[];
  register: (editor: any) => void;
}

export interface ValidationResult {
  isHydrationSafe: boolean;
  registeredPlugins: number;
  knownNodeTypes: string[];
  unknownNodeTypes: string[];
  errors: string[];
  warnings: string[];
  suggestions: string[];
}

export interface RegistrationLog {
  pluginName: string;
  success: boolean;
  nodeTypes: string[];
  error?: string;
}

// 🔌 PLUGIN REGISTRY CONTRACTS
export const PluginRegistryContracts: PluginContract[] = [
  CaptionNodeContract,
  ClauseNodeContract,
  TableNodeContract,
  LineNumberedParagraphContract,
  LegalSectionContract,
  LDSPCompletionContract,
  ParagraphIndentContract,
  LinkNodeContract,
  QuoteNodeContract,
  HeadingNodeContract,
  CodeNodeContract
];

// 🛡️ HYDRATION VALIDATOR CLASS
export class EdgeLexPluginValidator {
  private registeredContracts: PluginContract[] = [];
  private registrationLogs: RegistrationLog[] = [];
  private knownNodeTypes: Set<string> = new Set();

  constructor() {
    console.log('🚀 [EdgeLexPluginValidator] Initializing TypeScript plugin contract validator');
  }

  /**
   * ✅ Register all contracts via registerAllContracts(editor) before hydration
   */
  public registerAllContracts(editor: any): ValidationResult {
    console.log('🔌 [EdgeLexPluginValidator] Starting contract registration for', PluginRegistryContracts.length, 'plugins');
    
    this.registrationLogs = [];
    this.knownNodeTypes.clear();
    
    PluginRegistryContracts.forEach(contract => {
      try {
        console.log(`🔌 [EdgeLexPluginValidator] Registering ${contract.pluginName}...`);
        
        // Execute contract registration
        contract.register?.(editor);
        
        // Track successful registration
        this.registeredContracts.push(contract);
        contract.nodeTypes.forEach(nodeType => this.knownNodeTypes.add(nodeType));
        
        this.registrationLogs.push({
          pluginName: contract.pluginName,
          success: true,
          nodeTypes: contract.nodeTypes
        });
        
        console.log(`✅ [EdgeLexPluginValidator] ${contract.pluginName} registered successfully`);
        
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : String(error);
        console.warn(`⚠️ [EdgeLexPluginValidator] Plugin [${contract.pluginName}] failed during contract registration:`, errorMessage);
        
        this.registrationLogs.push({
          pluginName: contract.pluginName,
          success: false,
          nodeTypes: contract.nodeTypes,
          error: errorMessage
        });
      }
    });

    return this.createValidationSummary();
  }

  /**
   * ✅ Validate that all nodes in the editor's JSON content match known nodeTypes
   */
  public validateDocumentHydration(editorJsonContent: string): ValidationResult {
    console.log('🛡️ [EdgeLexPluginValidator] Starting document hydration validation');
    
    try {
      // Parse JSON safely
      let parsedContent: any;
      try {
        parsedContent = JSON.parse(editorJsonContent);
      } catch (parseError) {
        return {
          isHydrationSafe: false,
          registeredPlugins: this.registeredContracts.length,
          knownNodeTypes: Array.from(this.knownNodeTypes),
          unknownNodeTypes: [],
          errors: [`JSON parsing failed: ${parseError instanceof Error ? parseError.message : String(parseError)}`],
          warnings: [],
          suggestions: ['Check document JSON format']
        };
      }

      // Extract all node types from document
      const documentNodeTypes = new Set<string>();
      this.extractNodeTypes(parsedContent, documentNodeTypes);

      console.log('🔍 [EdgeLexPluginValidator] Found node types in document:', Array.from(documentNodeTypes));
      console.log('🔍 [EdgeLexPluginValidator] Known registered node types:', Array.from(this.knownNodeTypes));

      // Find unknown node types
      const unknownNodeTypes = Array.from(documentNodeTypes).filter(
        nodeType => !this.knownNodeTypes.has(nodeType)
      );

      const validationResult = this.createValidationResult(unknownNodeTypes);
      
      // Log validation result
      if (validationResult.isHydrationSafe) {
        console.log('✅ [EdgeLexPluginValidator] Document validation PASSED - safe for hydration');
      } else {
        console.warn('🚨 [EdgeLexPluginValidator] Document validation FAILED - blocking hydration');
        unknownNodeTypes.forEach(nodeType => {
          console.warn(`⚠️ [EdgeLexPluginValidator] Unknown node: \`${nodeType}\``);
        });
      }

      return validationResult;

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      console.error('🚨 [EdgeLexPluginValidator] Validation error:', errorMessage);
      
      return {
        isHydrationSafe: false,
        registeredPlugins: this.registeredContracts.length,
        knownNodeTypes: Array.from(this.knownNodeTypes),
        unknownNodeTypes: [],
        errors: [`Validation error: ${errorMessage}`],
        warnings: [],
        suggestions: []
      };
    }
  }

  /**
   * 🧠 After hydration, validate all expected nodeTypes from contracts were seen
   */
  public validatePostHydration(hydratedDocument: any): ValidationResult {
    console.log('🧠 [EdgeLexPluginValidator] Post-hydration validation starting');
    
    const documentNodeTypes = new Set<string>();
    this.extractNodeTypes(hydratedDocument, documentNodeTypes);
    
    const expectedNodeTypes = Array.from(this.knownNodeTypes);
    const seenNodeTypes = Array.from(documentNodeTypes);
    const missedNodeTypes = expectedNodeTypes.filter(nodeType => !documentNodeTypes.has(nodeType));
    
    console.log('🧠 [EdgeLexPluginValidator] Expected node types:', expectedNodeTypes);
    console.log('🧠 [EdgeLexPluginValidator] Seen node types:', seenNodeTypes);
    console.log('🧠 [EdgeLexPluginValidator] Missed node types:', missedNodeTypes);
    
    return {
      isHydrationSafe: true,
      registeredPlugins: this.registeredContracts.length,
      knownNodeTypes: expectedNodeTypes,
      unknownNodeTypes: [],
      errors: [],
      warnings: missedNodeTypes.length > 0 ? [`Some registered node types were not used: ${missedNodeTypes.join(', ')}`] : [],
      suggestions: []
    };
  }

  /**
   * 📊 Render a clear summary
   */
  public renderValidationSummary(result: ValidationResult): void {
    console.log('\n🔍 ===== EDGELEX PLUGIN VALIDATION SUMMARY =====');
    
    // Success indicators
    console.log(`✅ ${result.registeredPlugins} plugins registered`);
    console.log(`✅ ${result.knownNodeTypes.length} known nodeTypes handled`);
    
    // Unknown nodes warning
    if (result.unknownNodeTypes.length > 0) {
      result.unknownNodeTypes.forEach(nodeType => {
        console.warn(`⚠️ Unknown node: \`${nodeType}\``);
        const suggestion = this.suggestPlugin(nodeType);
        console.log(`➡️ Suggest: ${suggestion}`);
      });
    }
    
    // Errors
    if (result.errors.length > 0) {
      console.error('🚨 Errors:');
      result.errors.forEach(error => console.error(`   - ${error}`));
    }
    
    // Warnings
    if (result.warnings.length > 0) {
      console.warn('⚠️ Warnings:');
      result.warnings.forEach(warning => console.warn(`   - ${warning}`));
    }
    
    // Final status
    if (result.isHydrationSafe) {
      console.log('🎉 HYDRATION SAFE: Document can be safely hydrated');
    } else {
      console.error('🛑 HYDRATION BLOCKED: Unknown node types detected');
    }
    
    console.log('============================================\n');
  }

  /**
   * 🔍 Extract all node types from JSON recursively
   */
  private extractNodeTypes(node: any, nodeTypes: Set<string>): void {
    if (node && typeof node === 'object') {
      if (node.type && typeof node.type === 'string') {
        nodeTypes.add(node.type);
      }
      
      if (Array.isArray(node.children)) {
        node.children.forEach((child: any) => this.extractNodeTypes(child, nodeTypes));
      }
      
      // Handle other possible child properties
      Object.values(node).forEach(value => {
        if (Array.isArray(value)) {
          value.forEach(item => this.extractNodeTypes(item, nodeTypes));
        } else if (value && typeof value === 'object') {
          this.extractNodeTypes(value, nodeTypes);
        }
      });
    }
  }

  /**
   * 🎯 Suggest plugin for unknown node type
   */
  private suggestPlugin(nodeType: string): string {
    // Simple heuristic-based suggestions
    if (nodeType.toLowerCase().includes('caption')) {
      return 'Add plugin contract `CaptionNodePlugin`';
    } else if (nodeType.toLowerCase().includes('clause')) {
      return 'Add plugin contract `ClauseNodePlugin`';
    } else if (nodeType.toLowerCase().includes('table')) {
      return 'Add plugin contract `TableNodePlugin`';
    } else if (nodeType.toLowerCase().includes('legal')) {
      return 'Add plugin contract `LegalSectionPlugin`';
    } else if (nodeType.toLowerCase().includes('line') && nodeType.toLowerCase().includes('number')) {
      return 'Add plugin contract `LineNumberedParagraphPlugin`';
    } else {
      return `Add plugin contract \`${nodeType}Plugin\``;
    }
  }

  /**
   * 📋 Create validation result
   */
  private createValidationResult(unknownNodeTypes: string[]): ValidationResult {
    return {
      isHydrationSafe: unknownNodeTypes.length === 0,
      registeredPlugins: this.registeredContracts.length,
      knownNodeTypes: Array.from(this.knownNodeTypes),
      unknownNodeTypes,
      errors: unknownNodeTypes.length > 0 ? [`Unknown node types found: ${unknownNodeTypes.join(', ')}`] : [],
      warnings: [],
      suggestions: unknownNodeTypes.map(nodeType => this.suggestPlugin(nodeType))
    };
  }

  /**
   * 📈 Create validation summary from registration logs
   */
  private createValidationSummary(): ValidationResult {
    const successfulPlugins = this.registrationLogs.filter(log => log.success);
    const failedPlugins = this.registrationLogs.filter(log => !log.success);
    
    return {
      isHydrationSafe: failedPlugins.length === 0,
      registeredPlugins: successfulPlugins.length,
      knownNodeTypes: Array.from(this.knownNodeTypes),
      unknownNodeTypes: [],
      errors: failedPlugins.map(log => `${log.pluginName}: ${log.error}`),
      warnings: [],
      suggestions: []
    };
  }

  /**
   * 🔍 Get registration logs for debugging
   */
  public getRegistrationLogs(): RegistrationLog[] {
    return this.registrationLogs;
  }

  /**
   * 📊 Get current plugin state
   */
  public getPluginState() {
    return {
      registeredContracts: this.registeredContracts.map(c => c.pluginName),
      knownNodeTypes: Array.from(this.knownNodeTypes),
      registrationLogs: this.registrationLogs
    };
  }
}

// 🚀 MAIN EXPORT FUNCTIONS
export function createEdgeLexValidator(): EdgeLexPluginValidator {
  return new EdgeLexPluginValidator();
}

/**
 * 🔌 Legacy compatibility function
 */
export function registerAllContracts(editor: any): ValidationResult {
  const validator = createEdgeLexValidator();
  return validator.registerAllContracts(editor);
}

/**
 * 🛡️ Validate document for hydration safety
 */
export function validateDocumentHydration(editorJsonContent: string, validator?: EdgeLexPluginValidator): ValidationResult {
  const validatorInstance = validator || createEdgeLexValidator();
  return validatorInstance.validateDocumentHydration(editorJsonContent);
}

/**
 * 📋 Complete validation workflow
 */
export function performCompleteValidation(editor: any, editorJsonContent: string): ValidationResult {
  console.log('🚀 [EdgeLexPluginValidator] Starting complete validation workflow');
  
  const validator = createEdgeLexValidator();
  
  // Step 1: Register all contracts
  const registrationResult = validator.registerAllContracts(editor);
  validator.renderValidationSummary(registrationResult);
  
  // Step 2: Validate document hydration
  if (registrationResult.isHydrationSafe) {
    const hydrationResult = validator.validateDocumentHydration(editorJsonContent);
    validator.renderValidationSummary(hydrationResult);
    return hydrationResult;
  }
  
  return registrationResult;
}

// 🧪 DEBUGGING AND TESTING UTILITIES
export function debugPluginValidator(): void {
  const validator = createEdgeLexValidator();
  console.log('🔍 [EdgeLexPluginValidator] Debug info:');
  console.log('Available contracts:', PluginRegistryContracts.map(c => c.pluginName));
  console.log('Plugin state:', validator.getPluginState());
}